import { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Empty, Input, Row, Space, Spin, Typography, message } from 'antd';
import {
  EditOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  SolutionOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { getFormGroups } from '../services/dropdownApi';
import { loadDetailDefaults } from '../services/detailDefaults';
import { updateJobDescription } from '../services/jobsApi';
import { getDeep } from './form/payloadTransformer';
import AppButton from './AppButton';
import FieldValue from './detail/fieldRenderEngine';

const { Text } = Typography;

const sectionIcons = [
  <UserOutlined />,
  <EnvironmentOutlined />,
  <SolutionOutlined />,
  <InfoCircleOutlined />,
  <FileTextOutlined />,
];

const truthy = (value) => value === true || value === 1 || value === '1';

// Field keys that hold the job's free-text description (whichever the API uses).
const DESCRIPTION_KEYS = ['jobDescription', 'description', 'rawDescription', 'jobDescriptionText'];

// Pull the description field out of the raw form-groups (jobs module only).
function extractDescription(formGroups = []) {
  for (const group of formGroups) {
    for (const field of group.fields ?? []) {
      const matchesKey = DESCRIPTION_KEYS.includes(field.field);
      const matchesLabel = /description/i.test(field.label ?? '');
      if ((matchesKey || matchesLabel) && typeof field.value === 'string' && field.value.trim()) {
        return { key: field.field, value: field.value };
      }
    }
  }
  return null;
}

function normalizeGroups(formGroups = []) {
  return [...formGroups]
    .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
    .map((group) => ({
      ...group,
      fields: [...(group.fields ?? [])]
        .filter((field) => field.show === undefined || truthy(field.show))
        .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)),
    }))
    .filter((group) => group.fields.length);
}

function SectionTitle({ icon, title }) {
  return (
    <Space size={10} align="center">
      <div className="v1-section-icon-box">{icon}</div>
      <Text strong className="v1-section-label">{title}</Text>
    </Space>
  );
}

function RawDescription({ value }) {
  const text = String(value ?? '');
  if (/<[a-z][\s\S]*>/i.test(text)) {
    return (
      <div
        className="v1-detail-value"
        style={{ whiteSpace: 'normal', lineHeight: 1.7 }}
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );
  }
  return (
    <div className="v1-detail-value" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
      {text}
    </div>
  );
}

/*
 * DetailViewV1 — renders ONLY the left column of a detail view: the grouped
 * field cards plus (for jobs) the parsed Description card.
 *
 * Self-contained: given `moduleName` + `id` it fetches its own form-groups
 * (the backend embeds each field's value when an id is supplied) and manages
 * its own loading/error state. DetailViewPage just drops it into the left
 * <Col> and passes moduleName + id.
 */
export default function DetailViewV1({ moduleName, id, refreshKey = 0, onDescriptionSaved }) {
  const [formGroups, setFormGroups] = useState([]);
  const [loading, setLoading] = useState(Boolean(moduleName));
  const [error, setError] = useState('');
  const [descEditing, setDescEditing] = useState(false);
  const [descEditRaw, setDescEditRaw] = useState('');
  const [descSaving, setDescSaving] = useState(false);

  useEffect(() => {
    if (!moduleName) return undefined;

    let cancelled = false;

    async function fetchFormGroups() {
      try {
        setLoading(true);
        setError('');
        // Passing id makes the backend embed each field's actual record value.
        // view:'detail' makes the backend apply the admin Detail-Groups config
        // (field overrides + merges) and shape reference/document fields as
        // objects. Load the display defaults in parallel so FieldValue renders
        // with the configured S3 base / date format on first paint.
        const [data] = await Promise.all([
          getFormGroups({ module: moduleName, id, view: 'detail' }),
          loadDetailDefaults(),
        ]);
        if (!cancelled) setFormGroups(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(`Failed to fetch ${moduleName} detail fields:`, err);
        if (!cancelled) setError(err?.message || 'Failed to fetch detail fields');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchFormGroups();

    return () => {
      cancelled = true;
    };
  }, [moduleName, id, refreshKey]);

  const groups = useMemo(() => normalizeGroups(formGroups), [formGroups]);
  const isJob = moduleName === 'job';
  // For jobs we render the description in its own dedicated section (below),
  // so we keep it out of the normal field grid.
  const jobDescription = useMemo(
    () => (isJob ? extractDescription(formGroups) : null),
    [isJob, formGroups],
  );

  const handleDescEditStart = () => {
    setDescEditRaw(jobDescription?.value ?? '');
    setDescEditing(true);
  };

  const handleDescEditCancel = () => {
    setDescEditing(false);
    setDescEditRaw('');
  };

  const handleDescEditSave = async () => {
    if (!descEditRaw.trim()) {
      message.warning('Please enter a job description');
      return;
    }
    setDescSaving(true);
    try {
      await updateJobDescription(id, descEditRaw.trim());
      setDescEditing(false);
      setDescEditRaw('');
      setLoading(true);
      const data = await getFormGroups({ module: moduleName, id, view: 'detail' });
      setFormGroups(Array.isArray(data) ? data : []);
      message.success('Description updated and parsed successfully');
      onDescriptionSaved?.();
    } catch {
      message.error('Failed to update description');
    } finally {
      setDescSaving(false);
      setLoading(false);
    }
  };

  // renderField renders one labelled cell. `cellField` already carries its value
  // (the field config for flat groups; a per-row pseudo-field for addRow rows).
  const renderField = (cellField, keySuffix = '') => (
    <Col key={`${cellField.field}${keySuffix}`} xs={24} sm={12} lg={cellField.detailCol ?? cellField.col ?? 8}>
      <div className="v1-detail-field">
        <Text type="secondary" className="v1-detail-label">
          {cellField.label ?? cellField.field}
        </Text>
        <FieldValue field={cellField} />
      </div>
    </Col>
  );

  // For an addRow row, build a per-cell pseudo-field carrying that row's value.
  // File/document fields get the whole row as their value (so the render engine's
  // extractDocuments finds the stored file's location/name) and are forced to the
  // document render type; every other field reads its key off the row.
  const cellFieldForRow = (field, row) => {
    if (field.type === 'file' || field.type === 'document') {
      return { ...field, renderType: 'document', value: row };
    }
    return { ...field, value: getDeep(row, field.field) };
  };

  // Repeatable (addRow) group → one indexed sub-block per stored row. The backend
  // embeds the full row array on group.rows (workExperience / educationDetails /
  // documents). Renders nothing extra when there are no rows.
  const renderAddRowGroup = (group, index, fields) => {
    const key = group.name ?? group.id ?? String(index);
    const rows = Array.isArray(group.rows) ? group.rows : [];
    return (
      <Card
        size="small"
        key={key}
        className="v1-card v1-detail-card"
        title={<SectionTitle icon={sectionIcons[index % sectionIcons.length]} title={group.label ?? group.name ?? 'Details'} />}
      >
        {rows.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${group.label ?? 'records'} added`} />
        ) : (
          rows.map((row, rowIndex) => (
            <div key={rowIndex} className="v1-detail-addrow-item">
              {rows.length > 1 && (
                <Text strong className="v1-detail-addrow-index">
                  {(group.label ?? 'Record')} {rowIndex + 1}
                </Text>
              )}
              <Row gutter={[20, 18]}>
                {fields.map((field) => renderField(cellFieldForRow(field, row), `-${rowIndex}`))}
              </Row>
            </div>
          ))
        )}
      </Card>
    );
  };

  const renderGroup = (group, index) => {
    const key = group.name ?? group.id ?? String(index);
    // Hide the raw description field — it gets its own section for jobs.
    const fields = jobDescription
      ? group.fields.filter((field) => field.field !== jobDescription.key)
      : group.fields;
    if (!fields.length) return null;

    if (group.addRow) return renderAddRowGroup(group, index, fields);

    return (
      <Card
        size="small"
        key={key}
        className="v1-card v1-detail-card"
        title={<SectionTitle icon={sectionIcons[index % sectionIcons.length]} title={group.label ?? group.name ?? 'Details'} />}
      >
        <Row gutter={[20, 18]}>{fields.map((field) => renderField(field))}</Row>
      </Card>
    );
  };

  return (
    <Spin spinning={loading} tip="Loading detail fields...">
      <div className="v1-detail-left">
        {error && (
          <Alert
            type="error"
            showIcon
            message="Unable to load detail fields"
            description={error}
            className="v1-detail-alert"
          />
        )}

        {!loading && !error && groups.length === 0 && (
          <Empty description="No detail fields are configured." />
        )}
        {groups.map(renderGroup)}

        {/* Description — jobs module only, displayed exactly as saved. */}
        {isJob && jobDescription?.value && (
          <Card
            size="small"
            className="v1-card v1-detail-card"
            title={<SectionTitle icon={<FileTextOutlined />} title="Description" />}
            // extra={
            //   !descEditing && (
            //     <AppButton type="text" size="small" icon={<EditOutlined />} onClick={handleDescEditStart}>
            //       Edit
            //     </AppButton>
            //   )
            // }
          >
            {descEditing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{
                  background: '#ffffff',
                  border: '1px solid #d6e4f0',
                  borderRadius: 8,
                  padding: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}>
                  <Input.TextArea
                    rows={10}
                    value={descEditRaw}
                    onChange={(e) => setDescEditRaw(e.target.value)}
                    placeholder="Paste raw job description here..."
                    style={{
                      fontSize: 13,
                      lineHeight: 1.65,
                      border: 'none',
                      boxShadow: 'none',
                      resize: 'vertical',
                      padding: '12px 14px',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <AppButton variant="primary" size="small" loading={descSaving} onClick={handleDescEditSave}>
                    Save & Parse
                  </AppButton>
                  <AppButton size="small" onClick={handleDescEditCancel}>Cancel</AppButton>
                </div>
              </div>
            ) : (
              <RawDescription value={jobDescription.value} />
            )}
          </Card>
        )}
      </div>
    </Spin>
  );
}
